import { IAxiosInstance } from "../../../AbstractApiClient/AbstractApiClient";
import { TApiPaginatedResponse } from "../../../AbstractApiClient/IApiResponse";
import {
  TGetInvoicesResponseInvoice,
  type IGetInvoicesFilterParams,
} from "./getInvoicesRequest.schema";

/**
 * @description
 * Fetches all invoices for a company.
 * Optionally filters invoices by a search string (matches invoice number or customer name).
 * Supports cursor pagination with limit and start_at.
 * Supports sort_by and sort_direction.
 * Supports filter params (status, payment_status, date ranges) for correct totalCount.
 *
 * Sends GET request to la-forge route:
 * GET /api/invoices?companyID=...&search=...&limit=...&start_at=...&sort_by=...&sort_direction=...&status=...&invoiceDateFrom=...&etc
 *
 * @param axios - injected axios instance from AbstractApiClient
 * @param companyID - company ID for which to fetch invoices
 * @param search - optional search string to filter invoices
 * @param limit - optional limit for pagination (default: 50)
 * @param start_at - optional cursor for pagination
 * @param sort_by - optional sort field (e.g. invoiceDate)
 * @param sort_direction - optional sort order (asc | desc)
 * @param filterParams - optional filter params (status, payment_status, invoiceDateFrom/To, dueDateFrom/To)
 * @returns paginated list of invoices
 */
export const getInvoices =
  (axios: IAxiosInstance) =>
  async (
    companyID: string,
    search: string = "",
    limit?: number,
    start_at?: string,
    sort_by?: string,
    sort_direction?: "asc" | "desc",
    filterParams?: IGetInvoicesFilterParams
  ): Promise<TApiPaginatedResponse<TGetInvoicesResponseInvoice[]>> => {
    const params: Record<string, string | number> = { companyID, search };
    if (limit !== undefined) {
      params.limit = limit;
    }
    if (start_at) {
      params.start_at = start_at;
    }
    if (sort_by) {
      params.sort_by = sort_by;
    }
    if (sort_direction) {
      params.sort_direction = sort_direction;
    }
    if (filterParams) {
      if (filterParams.status) params.status = filterParams.status;
      if (filterParams.payment_status)
        params.payment_status = filterParams.payment_status;
      if (filterParams.invoiceDateFrom)
        params.invoiceDateFrom = filterParams.invoiceDateFrom;
      if (filterParams.invoiceDateTo)
        params.invoiceDateTo = filterParams.invoiceDateTo;
      if (filterParams.dueDateFrom)
        params.dueDateFrom = filterParams.dueDateFrom;
      if (filterParams.dueDateTo) params.dueDateTo = filterParams.dueDateTo;
    }

    const response = await axios.noRetry.get<
      TApiPaginatedResponse<TGetInvoicesResponseInvoice[]>
    >("/api/invoices", {
      params,
    });

    const resData = response.data;
    if (resData && typeof resData === "object" && "error" in resData) {
      throw new Error(
        (resData as { error?: string }).error || "Error fetching invoices"
      );
    }

    return response.data;
  };
